import {computed, observable, React} from '@api'
import {UNASSIGNED} from '@api/graphql'
import { IconName } from '@blueprintjs/core'
import {BorderNode, IJsonTabNode, TabNode, TabSetNode} from '@flexlayout'
import {ClickWorkspaceEventArgs, ICatalogItemDetailsPathElement, ISidebarState, VipLocalStore} from '@modules'
import {CatalogSerializableSettings, ICatalogUserSettings, icons, IFlexLayoutTabProps, site, StoresInstance, ui, useVipFlexLayoutTabStore} from '@stores'
import stringify from 'json-stable-stringify'
import {guid} from '@utils'
import {action, autorun, runInAction, when} from 'mobx'

export interface IJsonCatalogRootTabNode extends IJsonTabNode {
  config: IFlexLayoutCatalogRootConfig
}

export type FlexLayoutTabConfig = IBaseFlexLayoutTabConfig | IFlexLayoutIFrameTabConfig | IFlexLayoutCatalogRootConfig | IFlexLayoutToolbarWindowConfig | IFlexLayoutInlineCatalogItemDetailsConfig | ITrackedCatalogSidebarDetailsTabSettings

export interface IBaseFlexLayoutTabConfig {
  pin?: boolean
  nonDefaultName?: boolean
}

export interface IC8TabConfigSettings {
  injectCss?: string
  icon?: IconName

  subComponent?: 'collection-targets' | string
  queryString?: string

  /**
   * Something like C387635 or centric://REFLECTION/INSTANCE/_CS_Data/null
   */
  frameCentricUrl?: string

  /**
   * What tab spawned this?  We can be linked to a catalog and track their VLP hierarchy for example
   */
  parentTabId?: string
}

export interface IC8TabConfig extends IFlexLayoutIFrameTabConfig {
  settings: IC8TabConfigSettings
}

export type ItemVerticalAlign = 'top' | 'center' | 'bottom' | 'stretch'

export interface ITrackedCatalogSidebarDetailsTabSettings extends IFlexLayoutCatalogRootConfig {

}

export interface IFlexLayoutInlineCatalogItemDetailsConfig extends IBaseFlexLayoutTabConfig {
  settings: {
    verticalAlign?: ItemVerticalAlign
    orientation?: 'horz' | 'vert'
    workspace: string
    // deployment: string
    detailsPath: Array<ICatalogItemDetailsPathElement>
    selectedPathIndex?: number
    expanded?: boolean
    /**
     * The tab that created this one - it may later be deleted, but until then we can reuse their catalog stores
     */
    parentTab?: string
  }
}

export interface IFlexLayoutChangeHistoryConfig extends IBaseFlexLayoutTabConfig {
  settings: {
    workspace: string
    /**
     * The tab that created this one - it may later be deleted, but until then we can reuse their catalog stores
     */
    parentTab?: string
  }
}

export interface IFlexLayoutToolbarWindowConfig extends IBaseFlexLayoutTabConfig {
  settings: {
    isToggledOn?: boolean
  }
}

export interface IFlexLayoutIFrameTabConfig extends IBaseFlexLayoutTabConfig {
  settings: {
    url?: string,
    injectCss?: string
    icon?: IconName
  }
}

export interface IFlexLayoutCatalogRootConfig extends IBaseFlexLayoutTabConfig {
  settings: {
    catalog?: Partial<CatalogSerializableSettings>
    filter?: any
    groupBy?: {
      groupBy_orders?: string
      /**
       * Individual group paths, stuff like expansion {@see GroupByGrouping.settingsByGroup}
       */
      groupingSettings?: string
      byGroup?: string
      queryParams?: {
        /**
         * Overall settings {@see GroupByStore.settings }
         */
        groupBy?: string,
      }
    }
    sidebar?: Partial<ISidebarState>

    workspace?: string
    // deployment?: string
  }
}

export interface ICatalogRootTabJson extends IJsonTabNode {
  config: Partial<IFlexLayoutCatalogRootConfig>
}

export class CatalogRootFlexLayoutStore extends VipLocalStore {
  @observable hasFinishedFirstLoad = false

  _debounceHideToolbars
  constructor(stores: StoresInstance, public props: Partial<IFlexLayoutTabProps>) {
    super(stores)

    this._debounceHideToolbars = _.debounce(() => {
      if (this._lastScrollTop !== 0 && !site.currentOpenPopover) {
        this.hideToolbars = true
      }
    }, ui.settings.reports.hideToolbarsAfterScrollDelay)

    if (!props.flexNode.isPreviewLayout) {
      // console.trace(`Construct ${props.flexNode.id} - ${props.flexNode.name}`)

      this.stores.flexLayoutCatalogRoot = this
      this.registerEffects()
    }
  }

  @observable.ref scrollPanel: HTMLElement
  setScrollPanel = r => this.scrollPanel = r

  get vipTabStore() {
    return this.tab?.vipTabStore
  }

  get flexLayout() { return this.stores.flexLayout}

  get node() {
    const {flexLayout: {model}, props: {flexNode}} = this
    return model?.nodeById(flexNode?.id)
  }

  get tab(): TabNode { return this.node instanceof TabNode ? this.node : null}

  /**
   * Don't make computed as you can't trust prior values
   */
  get tabSet(): TabSetNode {
    const {node} = this

    if (node instanceof TabSetNode) return node

    const parent = node?.parent
    return parent?.attributes?.type === 'tabset'
           ? parent as TabSetNode
           : null
  }

  @observable hideToolbars = false // Start hidden?
  @observable private _lastScrollTop = 0
  @observable scrollDirection : 'up' | 'down' = 'up'

  @action onScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!ui.settings.hideHeaderAfterScrollDelay) return true

    const {currentTarget: {scrollTop}} = e,
      {_lastScrollTop} = this

    this.scrollDirection = scrollTop > _lastScrollTop ? 'down' : 'up'
    this._lastScrollTop = scrollTop
  }

  @computed get storesToInject() : StoresInstance {
    return this.tab?.storesToInject
  }

  @computed get showDropTarget() {
    const {storesToInject} = this
    if (!storesToInject) return false

    const {dragDrop, dragDrop: {isDragging, ddInteractions, dragType}} = storesToInject
    return isDragging && ddInteractions[dragType] != null && ddInteractions[dragType]['Catalog_CreateItems'] != null && dragDrop.dragItem.id != UNASSIGNED
  }

  /**
   * Don't make computed as you can't trust prior values
   */
  get border(): BorderNode {
    const {node} = this

    if (node instanceof BorderNode) return node

    const parent = node?.parent
    return parent?.attributes?.type === 'border'
           ? parent as BorderNode
           : null
  }

  get config() {
    return (this.tab?.attributes?.config as IFlexLayoutCatalogRootConfig)
  }

  //@computed get isForDifferentDeployment() { return this.config?.deployment && this.config?.deployment != this.stores.deployments.selectedDeploymentId }

  @computed get workspaceId() {
    return this.config.settings?.workspace
  }

  @action onContextMenu = (e) => {
    this.flexLayout.onContextMenu_forTabNode(this.tab)(e)
  }

  @computed get jsonNode(): IJsonTabNode {
    return this.props.flexNode.json
  }

  @computed get isActiveCatalogTab() {
    return this.flexLayout.activeTab === this.node
  }

  @observable hasRendered = false



  _toDispose = []

  @computed get stickyToolbars() {
    const {flexLayout, catalogRoot} = this
    return !flexLayout.isMaximized && !catalogRoot?.catalog?.isShowingAssortment
  }
  registerEffects = () => {
    const {stores, flexLayout} = this

    // console.debug(`[${stores.storeId}] - Mount Catalog Root Tab`, {viewMode: this.viewMode, visible: this.visible})

    this._toDispose = [
      when(() => !this.everVisible && this.visible, () => this.everVisible = true),
      when(() => this.stores.catalog?.hasFinishedFirstLoad, () => this.hasFinishedFirstLoad = true),
      autorun(() => {
        if (!this.stickyToolbars || !ui.settings.hideHeaderAfterScrollDelay) return

        const {_lastScrollTop, scrollDirection} = this, {isShowingPopover} = site
        runInAction(() => {
          const {hideToolbars} = this
          this.hideToolbars = false

          if (!isShowingPopover) {
            this._debounceHideToolbars()
          }
        })

      }, { name: `Show/Hide the toolbars depending on how the user just scrolled`}),
      // autorun(() => {
      //   // const {catalog: {focusedCatalogItemId}} = stores
      //   // if (focusedCatalogItemId) {
      //   //   flexLayout.activeTab = this.tab
      //   // }
      // }, { name: `Select our tab when an item in our catalog is selected`}),
      this.tab?.autorun_updateFlexNodeCustomSettings()
    ].filter(_.identity)
  }

  dispose = () => {
    // console.debug(`${this.debugId} - Unmount Catalog Root Tab`)
    this._toDispose.forEach(d => d())
    delete StoresInstance.allStores[this.stores.storeId]
  }

  @computed.struct get nextSettings() {
    const {
      tab,
      config
    } = this

    if (tab?.storesToInject) {
      const {
        storesToInject: {
          deployments: {selectedDeploymentId},
          catalog, groupBy, groupBy: {queryParams, groupingSettings: gbgs, nonDefaultSettings: gbs},
          catalog: {settingsStore}, sidebar: {serializableState: sidebar}, filter
        }
      } = tab
      if (catalog.initialized && groupBy.initialized && filter.hasLoaded && tab?.attributes?.config) {
        if (tab.attributes.config.deployment && tab.attributes.config.deployment != selectedDeploymentId) return config

        const newSettings: IFlexLayoutCatalogRootConfig = {
          ...tab.attributes.config,
          settings: {
            //deployment: selectedDeploymentId,
            ...(tab.attributes.config?.settings ?? {}),
            workspace: config?.settings?.workspace ?? catalog?.workspace?.id,
            catalog: _.cloneDeep(settingsStore.nonDefaultSettings),
            filter: _.cloneDeep(filter.queryParams),
            groupBy: {
              queryParams,
              groupingSettings: Base64.encode(JSON.stringify(gbgs)),
              byGroup: _.cloneDeep(gbs)
            },
            sidebar
          }
        }

        return newSettings
      }
    }

    return config
  }

  @computed get debugId() {
    const {tab, stores: {storeId}, viewMode} = this
    return `[${storeId}] - ${tab?.name ? `[${tab.name}] - [${tab.id}]` : '[No FlexNode]'} - [${viewMode}]`
  }

  @observable everVisible = false

  @computed get visible() {
    const {tabSet, border, node, jsonNode, flexLayout} = this

    if (!tabSet || !jsonNode) return false

    const {isMaximized, maximizedTab} = flexLayout

    if (isMaximized && this.node != maximizedTab) return false

    return border != null || tabSet.selectedNode?.id === node.id || !tabSet.selectedNode
  }

  @computed get catalogRoot() {
    return this.tab?.catalogRoot
  }

  @action onSwitchTabWorkspace = (e: React.FormEvent, {workspace, preset, assortment }: ClickWorkspaceEventArgs) => {
    // Prevent default behavior or else react-router won't update smoothly
    e.stopPropagation()
    e.preventDefault()

    console.debug(`onSwitchTabWorkspace`, workspace)
    const {catalogRoot, tab, stores: {workspaces, deployments: {selectedDeploymentId}}} = this,
      config = tab.config as IFlexLayoutCatalogRootConfig

    const existingFilters = _.cloneDeep(catalogRoot.filter.filtersWithSelections)

    _.merge(config, {settings: {
        workspace: workspace._id,
        catalog: {
          preset: preset ? preset._id: undefined,
          assortment: assortment ? assortment._id : undefined
          //deployment: selectedDeploymentId,
        }
      }} as IFlexLayoutCatalogRootConfig)

    delete config.settings.catalog.cardView
    catalogRoot.stores._workspace = workspaces.wrapperById[workspace._id]
    this.stores.presets.loadPreset(workspace, null, true)

    debugger
  }

  @computed get tabIndex() {
    const {tabSet, node} = this

    if (!tabSet || !node) return -1

    return node.indexInParent
  }

  @computed get icon() {
    const {viewMode, tab} = this, selected = tab?.isSelected
    switch (this.viewMode) {
      case 'card':
        return selected ? icons.card : null

      case 'pdf':
        return icons.pdf

      case 'report':
        return selected ? icons.reportView: null

      case 'table':
        return selected ? icons.table : null
    }

    return null
  }

  @computed get iconTooltip() {
    const {strings} = this
    switch (this.viewMode) {
      case 'card':
        return strings.cardView

      case 'pdf':
        return 'PDF View'

      case 'report':
        return strings.reportView

      case 'table':
        return strings.tableView
    }

    return ''
  }

  @computed get viewMode() { return this.catalog.settings.viewMode ?? 'card' }

  @computed get isCardView() { return this.viewMode == 'card'}

  @computed get isTableView() { return this.viewMode == 'table'}

  @computed get isPdfView() { return this.viewMode == 'pdf'}

  @computed get tabName() { return this.tab?.attributes?.name}

  @computed get isRightNeighborShowingDetailPanes() {
    const {tabSet: {neighborRight}} = this
    return neighborRight?.children?.some(t => t instanceof TabNode && t.component === 'inlineCatalogItemDetails')
  }

  /**
   * Creates a new child tab relative to this tab to the right or below us depending on contextual layout
   * {@see openCatalogItemDetailsInNewTab}
   * @param newTabJson
   */
  @action addNewChildTab = (newTabJson) => {
    const {
      tab, tabSet, config, flexLayout: {moveTab},
      tabSet: {neighborRight}
    } = this
    const newTab = tabSet.addChildJson(newTabJson, false, tab.indexInParent + 1)
    moveTab(newTab, neighborRight?.isLayoutSidebar ? 'down' : 'right')

    return newTab
  }
}

export const CatalogRootFlexLayoutStoreContext = React.createContext<CatalogRootFlexLayoutStore>(null)
export const useCatalogRootFlexLayoutStore = () => {
  const tab = useVipFlexLayoutTabStore(),
    direct = React.useContext(CatalogRootFlexLayoutStoreContext)


  return tab?.tab?.catalogRoot ?? direct
}



// WEBPACK FOOTER //
// ./src/stores/catalog/flexLayout/flexComponents/content-panels/CatalogRootFlexLayoutStore.tsx